import { useState } from 'react';
import { ArrowRight } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';
import phone from '../../../assets/images/flashcards-phone.png';

const TABS = [
  {
    tab: 'Concept Highlighting',
    title: 'Smart Concept Highlighting',
    body: 'Key definitions, dates, formulas and terms are automatically detected and highlighted so you never miss what matters.',
  },
  {
    tab: 'Flashcards',
    title: 'Auto-Built Flashcards',
    body: 'Every note instantly becomes a deck of flashcards — pulled straight from your material so revision writes itself.',
  },
  {
    tab: 'Quizzes',
    title: 'Practice Quizzes',
    body: 'Turn any note into an adaptive quiz with instant feedback, so you know exactly what to review before the exam.',
  },
  {
    tab: 'Smart Summary',
    title: 'One-Page Smart Summary',
    body: 'Get a concise, one-page overview of any chapter — the big picture and the details that matter, distilled.',
  },
  {
    tab: 'Ask Andy',
    title: 'Ask Andy Anything',
    body: 'Chat with your notes. Andy answers questions grounded in your own material, not generic internet answers.',
  },
  {
    tab: 'Collaborate',
    title: 'Study Together',
    body: 'Share notes and decks with classmates, compare progress, and keep each other accountable in real time.',
  },
];

const NotesFeatures = () => {
  const [active, setActive] = useState(0);
  const cur = TABS[active];

  return (
    <section className="relative py-20 lg:py-24">
      <div className="glow left-1/4 top-1/3 h-[400px] w-[520px] bg-accent/8" />
      <Container className="relative">
        <Reveal className="text-center">
          <span className="eyebrow">What You Get</span>
          <h2 className="font-heading mx-auto mt-5 max-w-3xl text-[2rem] font-bold leading-[1.1] sm:text-[2.6rem] lg:text-[3rem]">
            <span className="head-fade">Notes That Actually </span>
            <span className="text-accent-grad">Do The Work For You</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[0.95rem] text-white/55">
            Every Interactive Note comes packed with intelligent features designed
            to make studying faster and more effective.
          </p>
        </Reveal>

        <Reveal delay={120} className="mt-10">
          <div className="card-dark overflow-hidden rounded-2xl">
            {/* tabs */}
            <div className="no-scrollbar flex overflow-x-auto border-b border-white/8">
              {TABS.map((t, i) => (
                <button
                  key={t.tab}
                  onClick={() => setActive(i)}
                  className={`relative shrink-0 px-5 py-4 text-[0.85rem] font-medium transition-colors ${
                    active === i ? 'text-white' : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  {t.tab}
                  <span
                    className={`absolute -bottom-px left-3 right-3 h-0.5 rounded-full bg-accent transition-opacity duration-300 ${
                      active === i ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* content */}
            <div className="grid items-center gap-8 p-6 md:p-10 lg:grid-cols-2">
              <div key={cur.tab} className="animate-fade-up">
                <h3 className="font-heading text-[1.4rem] font-semibold text-white md:text-[1.8rem]">
                  {cur.title}
                </h3>
                <p className="mt-3 max-w-lg text-[0.95rem] leading-relaxed text-white/60">
                  {cur.body}
                </p>
                <a
                  href="https://app.schoolhack.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary mt-6 px-5 py-2.5 text-[0.85rem]"
                >
                  Try now
                  <ArrowRight size={15} />
                </a>
              </div>
              <div className="relative mx-auto w-full max-w-[360px]">
                <div className="glow left-1/2 top-1/2 h-[240px] w-[260px] -translate-x-1/2 -translate-y-1/2 bg-accent/14" />
                <img
                  src={phone}
                  alt="Interactive note feature preview"
                  className="relative w-full animate-float object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

export default NotesFeatures;
